import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Cloud,
  CheckCircle,
  XCircle,
  ExternalLink,
  Loader2,
  Shield,
  Server,
  Zap,
  AlertCircle,
  LinkIcon,
  Unlink,
} from 'lucide-react'
import { useAuthStore } from '@/stores/auth'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import toast from 'react-hot-toast'

export function CloudflarePage() {
  const { user, setUser } = useAuthStore()
  const queryClient = useQueryClient()

  const isConnected = !!user?.cloudflareAccountId

  const { data: cfStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['cloudflare-status'],
    queryFn: () => api.getCloudflareStatus(),
    enabled: isConnected,
  })

  const disconnectMutation = useMutation({
    mutationFn: () => api.disconnectCloudflare(),
    onSuccess: () => {
      if (user) {
        setUser({
          ...user,
          cloudflareAccountId: null,
          cloudflareConnected: false,
        })
      }
      queryClient.invalidateQueries({ queryKey: ['user'] })
      queryClient.invalidateQueries({ queryKey: ['cloudflare-status'] })
      toast.success('Cloudflare disconnected')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const handleConnect = () => {
    // Redirect to Cloudflare OAuth
    window.location.href = '/api/auth/cloudflare/connect'
  }

  const handleDisconnect = () => {
    if (
      confirm(
        'Are you sure you want to disconnect Cloudflare? Your deployed sites will continue to work, but you won\'t be able to deploy new sites.'
      )
    ) {
      disconnectMutation.mutate()
    }
  }

  const features = [
    {
      icon: Server,
      title: 'Domain Verification',
      description: 'Automatically verify domain ownership via DNS',
    },
    {
      icon: Zap,
      title: 'One-Click Deploy',
      description: 'Deploy generated sites to Cloudflare Pages instantly',
    },
    {
      icon: Shield,
      title: 'Free SSL',
      description: 'All deployed sites get free SSL certificates',
    },
  ]

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/dashboard/settings" className="btn-ghost p-2">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-display font-bold">Cloudflare Integration</h1>
          <p className="text-muted-foreground">
            Connect your Cloudflare account to deploy sites
          </p>
        </div>
      </div>

      {/* Connection Status */}
      <div className={cn(
        'card border-2 transition-colors',
        isConnected ? 'border-success/50 bg-success/5' : 'border-border'
      )}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className={cn(
            'p-4 rounded-xl',
            isConnected ? 'bg-success/10' : 'bg-muted'
          )}>
            <Cloud className={cn(
              'h-10 w-10',
              isConnected ? 'text-success' : 'text-muted-foreground'
            )} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {isConnected ? (
                <CheckCircle className="h-5 w-5 text-success" />
              ) : (
                <XCircle className="h-5 w-5 text-muted-foreground" />
              )}
              <span className="font-semibold">
                {isConnected ? 'Connected' : 'Not Connected'}
              </span>
            </div>
            {isConnected && user?.cloudflareAccountId ? (
              <p className="text-sm text-muted-foreground mt-1">
                Account ID: {user.cloudflareAccountId.slice(0, 8)}...
              </p>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">
                Connect your Cloudflare account to get started
              </p>
            )}
          </div>
          {isConnected ? (
            <button
              onClick={handleDisconnect}
              disabled={disconnectMutation.isPending}
              className="btn-outline text-destructive hover:bg-destructive/10"
            >
              {disconnectMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Unlink className="h-4 w-4" />
              )}
              Disconnect
            </button>
          ) : (
            <button onClick={handleConnect} className="btn-primary">
              <LinkIcon className="h-4 w-4" />
              Connect Cloudflare
            </button>
          )}
        </div>
      </div>

      {/* Status Details (when connected) */}
      {isConnected && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Account Status</h2>

          {statusLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Zones</p>
                <p className="text-2xl font-display font-bold">
                  {cfStatus?.zones || 0}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Pages Projects</p>
                <p className="text-2xl font-display font-bold">
                  {cfStatus?.pagesProjects || 0}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Workers</p>
                <p className="text-2xl font-display font-bold">
                  {cfStatus?.workers || 0}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Features */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">What You Get</h2>
        <div className="grid gap-6 sm:grid-cols-3">
          {features.map((feature) => (
            <div key={feature.title} className="text-center">
              <div className="inline-flex p-3 rounded-xl bg-primary/10 mb-3">
                <feature.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-1">{feature.title}</h3>
              <p className="text-sm text-muted-foreground">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* How It Works */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">How It Works</h2>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-sm">
              1
            </div>
            <div>
              <p className="font-medium">Connect your Cloudflare account</p>
              <p className="text-sm text-muted-foreground">
                Click "Connect Cloudflare" to authorize access via OAuth. We only
                request the minimum permissions needed.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-sm">
              2
            </div>
            <div>
              <p className="font-medium">Add domains with Cloudflare nameservers</p>
              <p className="text-sm text-muted-foreground">
                Your domains should be using Cloudflare nameservers for automatic
                verification and deployment.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-sm">
              3
            </div>
            <div>
              <p className="font-medium">Generate and deploy sites</p>
              <p className="text-sm text-muted-foreground">
                When you deploy a site, we create a Cloudflare Pages project and
                configure the custom domain automatically.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Permissions */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Permissions Requested</h2>
        <div className="space-y-3">
          {[
            {
              permission: 'Zone:Read',
              description: 'List your domains to verify ownership',
            },
            {
              permission: 'DNS:Edit',
              description: 'Add DNS records for domain verification',
            },
            {
              permission: 'Pages:Edit',
              description: 'Create and manage Cloudflare Pages projects',
            },
            {
              permission: 'Workers:Read',
              description: 'View worker deployments (read-only)',
            },
          ].map((item) => (
            <div key={item.permission} className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-medium font-mono text-sm">{item.permission}</p>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* External Link */}
      <div className="flex items-start gap-4 p-4 rounded-lg bg-muted/50">
        <AlertCircle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium">Need a Cloudflare account?</p>
          <p className="text-sm text-muted-foreground">
            Cloudflare offers a generous free tier that's perfect for domain
            parking sites.
          </p>
        </div>
        <a
          href="https://dash.cloudflare.com/sign-up"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-outline btn-sm"
        >
          <ExternalLink className="h-4 w-4" />
          Sign Up
        </a>
      </div>
    </div>
  )
}
